import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';

import { Note } from 'src/services/api/notesApi';

import {
  NoteModal,
  NoteModalHandlerProps,
  NoteModalStateProps,
} from './NoteModal';

type NoteModalProps = Partial<NoteModalHandlerProps> & NoteModalStateProps;

type NoteModalContextType = Dispatch<SetStateAction<NoteModalProps>>;

const NoteModalContext = createContext<NoteModalContextType | undefined>(
  undefined,
);

export const NoteModalProvider = ({ children }: { children: ReactNode }) => {
  const [{ onClose, onDelete, onSave, ...noteModalState }, setNoteModalState] =
    useState<NoteModalProps>({
      open: false,
      note: null,
    });

  const close = useCallback(() => {
    setNoteModalState((prev) => ({ ...prev, open: false }));
  }, [setNoteModalState]);

  return (
    <>
      <NoteModalContext.Provider value={setNoteModalState}>
        {children}
      </NoteModalContext.Provider>
      <NoteModal
        {...noteModalState}
        onClose={() => {
          if (onClose) onClose();
          close();
        }}
        onDelete={(note) => {
          if (onDelete) onDelete(note);
          close();
        }}
        onSave={(title, content, note) => {
          if (onSave) onSave(title, content, note);
          close();
        }}
      />
    </>
  );
};

export const useNoteModal = ({
  onClose,
  onDelete,
  onSave,
}: Partial<NoteModalHandlerProps>) => {
  const setNoteModalState = useContext(NoteModalContext);
  if (setNoteModalState === undefined) {
    throw new Error('useNoteModal must be used within a NoteModalProvider');
  }

  const open = useCallback(
    (note: Note | null) => {
      setNoteModalState({ onClose, onDelete, onSave, note, open: true });
    },
    [onClose, onDelete, onSave],
  );

  return open;
};
